import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Server, TrendingDown, Zap, Shield, BarChart3, Sparkles, Sun, Moon, Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { GoogleLogin } from '@react-oauth/google';

function Login({ setIsAuthenticated }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    mobile_number: '',
    referral_code: ''
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Pre-fill and lock referral code from URL query param
  const referralFromUrl = new URLSearchParams(location.search).get('referral') || '';
  const isReferralLocked = !!referralFromUrl;

  useEffect(() => {
    if (referralFromUrl) {
      setFormData(prev => ({ ...prev, referral_code: referralFromUrl.toUpperCase() }));
      setIsLogin(false);
    } else if (location.pathname === '/signup') {
      setIsLogin(false);
    }
  }, []);


  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { email: formData.email });
      toast.success('OTP sent to your email!');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/send-otp`, { email: formData.email });
      toast.success('OTP sent to your email!');
      setForgotStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: formData.email, otp, new_password: newPassword });
      toast.success('Password reset successfully! Please sign in.');
      setForgotPassword(false);
      setForgotStep(1);
      setOtp('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (forgotPassword) {
        if (forgotStep === 1) { await handleForgotSendOtp(); return; }
        if (forgotStep === 2) {
          await axios.post(`${API}/auth/verify-otp`, { email: formData.email, otp });
          toast.success('OTP verified!');
          setForgotStep(3);
          setLoading(false);
          return;
        }
        if (forgotStep === 3) { await handleResetPassword(); return; }
      }

      if (!isLogin) {
        // Verify OTP then register
        await axios.post(`${API}/auth/verify-otp`, { email: formData.email, otp });
      }

      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      setIsAuthenticated(true);
      window.dispatchEvent(new CustomEvent('optops-auth-changed'));
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { credential: credentialResponse.credential });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setIsAuthenticated(true);
      window.dispatchEvent(new CustomEvent('optops-auth-changed'));
      toast.success('Welcome!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google sign-in failed');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 flex relative font-display antialiased">
      {/* Subtle dot-grid texture, not gradient blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '24px 24px',
          color: 'currentColor',
        }}
      />

      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        className="absolute top-6 right-6 z-30 p-2 rounded-lg text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors"
        title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Left rail — brand + quiet credibility, no marketing stats */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] xl:w-[40%] px-12 xl:px-16 py-14 bg-zinc-50 dark:bg-zinc-900/40 border-r border-zinc-200 dark:border-zinc-800 relative z-10">
        <div>
          <div className="flex items-center gap-4">
            <img
              src="/optops-logo.png"
              alt="OptOps"
              className="h-28 w-auto dark:invert dark:hue-rotate-180"
            />
            <span className="text-zinc-900 dark:text-white font-bold tracking-tight text-5xl">
              OptOps<span className="text-zinc-400 dark:text-zinc-500 font-semibold">.Ai</span>
            </span>
          </div>

          <h1 className="mt-16 text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-900 dark:text-white leading-[1.1]">
            Kubernetes cost optimization
            <span className="block text-zinc-400 dark:text-zinc-500 font-normal mt-2">
              that doesn't break production.
            </span>
          </h1>

          <p className="mt-6 text-zinc-600 dark:text-zinc-400 text-base leading-relaxed max-w-sm">
            Closed-loop rightsizing, smooth node optimization, and provable
            zero-downtime — across EKS, GKE, AKS, and self-managed clusters.
          </p>

          <ul className="mt-12 space-y-4 text-sm text-zinc-700 dark:text-zinc-300">
            {[
              'PDB-respecting drains by default',
              'OOM-aware memory controls',
              'Per-cluster optimization profiles',
              'ISO 27001 certified',
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-1 h-1 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-zinc-500 dark:text-zinc-500">
          <div className="flex items-center gap-4">
            <a href="https://optops.ai" className="hover:text-zinc-900 dark:hover:text-white transition-colors">optops.ai</a>
            <a href="https://optops.ai/privacy-policy" className="hover:text-zinc-900 dark:hover:text-white transition-colors">Privacy</a>
            <a href="https://optops.ai/terms-of-service" className="hover:text-zinc-900 dark:hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </div>

      {/* Right side — auth form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-sm">
          {/* Mobile brand (hidden on lg) */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <img
              src="/optops-logo.png"
              alt="OptOps"
              className="h-20 w-auto dark:invert dark:hue-rotate-180"
            />
            <span className="text-zinc-900 dark:text-white font-bold text-4xl">OptOps<span className="text-zinc-400 dark:text-zinc-500 font-semibold">.Ai</span></span>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            {forgotPassword
              ? forgotStep === 3
                ? 'Set a new password'
                : forgotStep === 2
                ? 'Verify your email'
                : 'Reset your password'
              : isLogin
              ? 'Sign in'
              : 'Create your account'}
          </h2>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
            {forgotPassword
              ? forgotStep === 1
                ? 'We’ll send a 6-digit code to your email.'
                : forgotStep === 2
                ? `Code sent to ${formData.email}.`
                : 'Choose a strong password.'
              : isLogin
              ? 'Welcome back. Sign in to continue.'
              : 'Free during private beta.'}
          </p>

          <div className="mt-8">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Full Name</Label>
                  <Input
                    id="full_name"
                    data-testid="full-name-input"
                    placeholder="John Doe"
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>
              )}

              {!forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Email Address</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder="you@company.com"
                      className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                      value={formData.email}
                      onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (!isLogin) setOtpSent(false); }}
                      required
                    />
                    {!isLogin && (
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading || !formData.email}
                        className="h-10 px-4 whitespace-nowrap bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:hover:bg-zinc-200 text-white dark:text-zinc-900 font-medium"
                      >
                        {loading && !otpSent ? <Loader2 className="h-4 w-4 animate-spin" /> : otpSent ? 'Resend' : 'Send OTP'}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {(forgotPassword && forgotStep === 1) && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              )}

              {!isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="mobile_number" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Mobile Number <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                  <Input
                    id="mobile_number"
                    data-testid="mobile-number-input"
                    type="tel"
                    placeholder="+1234567890"
                    className={`h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 ${formData.mobile_number && !/^\+?\d{7,15}$/.test(formData.mobile_number) ? 'border-red-500' : ''}`}
                    value={formData.mobile_number}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\+?\d*$/.test(val)) {
                        setFormData({ ...formData, mobile_number: val });
                      }
                    }}
                  />
                  {formData.mobile_number && !/^\+?\d{7,15}$/.test(formData.mobile_number) && (
                    <p className="text-xs text-red-500">Enter a valid mobile number (7–15 digits, optional + prefix)</p>
                  )}
                </div>
              )}

              {!isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="referral_code" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Referral Code {!isReferralLocked && <span className="text-slate-400 font-normal">(optional)</span>}</Label>
                  <Input
                    id="referral_code"
                    data-testid="referral-code-input"
                    type="text"
                    placeholder="OPT-XXXXXXXX"
                    className={`h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 ${isReferralLocked ? 'bg-slate-100 dark:bg-slate-800 cursor-not-allowed opacity-75' : ''}`}
                    value={formData.referral_code}
                    onChange={(e) => !isReferralLocked && setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                    readOnly={isReferralLocked}
                  />
                </div>
              )}

              {isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Password</Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => { setForgotPassword(true); setForgotStep(1); setOtp(''); setNewPassword(''); }}
                    className="text-xs text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white underline underline-offset-2"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Password</Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              )}

              {!isLogin && otpSent && !forgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Enter OTP sent to {formData.email}</Label>
                  <Input
                    id="otp"
                    data-testid="otp-input"
                    type="text"
                    placeholder="123456"
                    maxLength={6}
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 text-center text-lg tracking-widest"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                  />
                </div>
              )}

              {forgotPassword && forgotStep === 2 && (
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">Enter OTP sent to {formData.email}</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="123456"
                    maxLength={6}
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300 text-center text-lg tracking-widest"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                  />
                </div>
              )}

              {forgotPassword && forgotStep === 3 && (
                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    placeholder="••••••••"
                    className="h-10 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-zinc-300 focus:ring-1 focus:ring-zinc-900 dark:focus:ring-zinc-300"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              <Button
                data-testid="submit-button"
                type="submit"
                className="w-full h-10 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:hover:bg-zinc-200 text-white dark:text-zinc-900 font-medium transition-colors mt-6"
                disabled={loading || (!isLogin && !otpSent && !forgotPassword)}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : isLogin && !forgotPassword ? (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Sign In
                  </>
                ) : forgotPassword && forgotStep === 1 ? (
                  <>
                    <Mail className="mr-2 h-5 w-5" />
                    Send OTP
                  </>
                ) : forgotPassword && forgotStep === 2 ? (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Verify OTP
                  </>
                ) : forgotPassword && forgotStep === 3 ? (
                  <>
                    <Shield className="mr-2 h-5 w-5" />
                    Reset Password
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Sign Up
                  </>
                )}
              </Button>
            </form>

            {!forgotPassword && (
              <div className="mt-6">
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200 dark:border-zinc-800" /></div>
                  <div className="relative flex justify-center text-sm"><span className="bg-white dark:bg-zinc-950 px-3 text-zinc-500 dark:text-zinc-500 text-xs uppercase tracking-wide">or</span></div>
                </div>
                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast.error('Google sign-in failed')}
                    theme={theme === 'dark' ? 'filled_black' : 'outline'}
                    size="large"
                    width="100%"
                    text={isLogin ? 'signin_with' : 'signup_with'}
                  />
                </div>
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                data-testid="toggle-auth-mode"
                type="button"
                onClick={() => { setIsLogin(!isLogin); setOtpSent(false); setOtp(''); setForgotPassword(false); setForgotStep(1); setNewPassword(''); }}
                className="text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white font-medium transition-colors"
              >
                {isLogin
                  ? "Don't have an account? Sign up →"
                  : '← Already have an account? Sign in'}
              </button>
              {forgotPassword && (
                <button
                  type="button"
                  onClick={() => { setForgotPassword(false); setForgotStep(1); setOtp(''); setNewPassword(''); }}
                  className="block mx-auto mt-2 text-sm text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white font-medium transition-colors"
                >
                  ← Back to Sign In
                </button>
              )}
            </div>

            {!isLogin && (
              <p className="text-xs text-center text-zinc-500 dark:text-zinc-500 mt-6 leading-relaxed">
                By creating an account, you agree to our{' '}
                <a href="https://optops.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-zinc-900 dark:text-zinc-200 underline underline-offset-2 hover:text-zinc-700 dark:hover:text-white">Terms of Service</a>{' '}
                and{' '}
                <a href="https://optops.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-zinc-900 dark:text-zinc-200 underline underline-offset-2 hover:text-zinc-700 dark:hover:text-white">Privacy Policy</a>.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
